// ClipRadar marketing site — full page assembly.
import { useEffect } from 'react';
import { SiteNav, type ShowcaseItem } from './sections/hero';
import { FeatureShowcase } from './sections/feature-showcase-v2';
import { Hero } from './sections/hero-v2';
import { FilenameWall } from './sections/filename-wall';
import { UltraSearch } from './sections/ultra';
import { HowItWorks, PremiereCapabilities, PremiereSection } from './sections/works';
import { Breather, DesktopApp, Testimonials, WhyBento } from './sections/social';
import { FAQ, Pricing, Roadmap } from './sections/closing';
import { MultilingualStrip } from './sections/multilingual';
import { FinalCTA } from './sections/final-lantern';
import { LaunchFilm } from './sections/launch-film';
import { Compare } from './sections/compare';
import { AccentCTA } from './sections/accent-cta';
import { BigFooter } from './sections/footer';

const visual: ShowcaseItem[] = [
  { icon: 'search', title: 'Natural language search', text: 'Type what you remember — “astronauts working on the lunar surface” — and the perfect shot surfaces instantly.', scene: 'visual-search' },
  { icon: 'layers', title: 'Context picker', text: 'Choose exactly which media to search: a folder, a drive, a bin — or everything you’ve ever indexed.', scene: 'context-picker' },
  { icon: 'image', title: 'Search by image', text: 'Drop in a reference frame or a still. ClipRadar finds every shot that looks like it.', scene: 'image-search' },
  { icon: 'sparkle', title: 'Match similar', text: 'Found one good take? One click surfaces every visually similar moment across your library.', scene: 'match-similar' },
];
const speech: ShowcaseItem[] = [
  { icon: 'mic', title: 'Search spoken words', text: 'Every word in your footage is transcribed and indexed. Search a quote, jump straight to the moment it was said.', scene: 'speech-search' },
  { icon: 'text', title: 'Open the transcript', text: 'Open the transcript window and select the lines you want — in and out points set themselves.', scene: 'transcript' },
  { icon: 'plus', title: 'Add to timeline', text: 'Send the selection straight to your timeline. No scrubbing, no markers, no re-watching.', scene: 'add-timeline' },
  { icon: 'globe', title: '110+ languages', text: 'Footage in Spanish, Japanese, Arabic — it all gets transcribed. And you can ask in your language, whatever the footage speaks.', scene: 'languages' },
];
const faces: ShowcaseItem[] = [
  { icon: 'user', title: 'Find every appearance', text: 'Pick a face and see every frame they appear in — across your entire library, not just one project.', scene: 'faces-search' },
  { icon: 'users', title: 'Combine faces — any or all', text: 'Find every two-shot of your hosts together, or any clip where either appears. One query.', scene: 'faces-combine' },
  { icon: 'search', title: 'Type their name', text: 'Name a person once. From then on, just type “Kennedy” right in the search bar.', scene: 'faces-name' },
  { icon: 'layers', title: 'Manage your collection', text: 'A tidy people library — merge duplicates, rename, keep collections per project.', scene: 'faces-manage' },
];

export function App() {
  // scroll-reveal: every section fades up on first view
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const els = [...document.querySelectorAll('section')];
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('crw-in'); io.unobserve(e.target); } });
    }, { threshold: 0.08 });
    els.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight) { el.classList.add('crw-in'); }
      el.classList.add('crw-reveal');
      io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  return (
    <div>
      <SiteNav />
      {/* single <main> landmark — screen-reader navigation + Lighthouse a11y */}
      <main>
      <Hero />
      <FeatureShowcase id="visual" label="Visual search" title="Describe the shot. Get the shot."
        sub={'ClipRadar has watched every frame you’ve indexed. Search your footage the way you remember it — by what’s on screen.'} items={visual} />
      <FeatureShowcase id="speech" flip label="Speech search" title="Find anything anyone ever said."
        sub={'Every word, transcribed and indexed on your machine. Search a half-remembered quote and land on the exact frame.'} items={speech} />
      {/* the breather between the twin showcases — true garnish: 110+ languages */}
      <MultilingualStrip />
      <FeatureShowcase id="faces" label="Faces search" title={'Search by who’s on screen.'}
        sub={'ClipRadar recognizes the people in your footage and keeps a private, local collection — so “every shot of Sarah” is a query, not an afternoon.'} items={faces} />
      <UltraSearch />
      <HowItWorks />
      <PremiereSection />
      <PremiereCapabilities />
      {/* two breathers + the restored big Testimonials (founder, 2026-07-17):
          Tomasz's panel quote right after the panel proof, the big section (with
          the beta band inside it) after the desktop app, Sofia before Pricing */}
      <Breather name="Tomasz W." />
      {/* agentic-editing section REMOVED (founder, 2026-07-17): the MCP feature is
          real but not polished enough to sell yet — reintroduce when it's proud */}
      <DesktopApp />
      {/* launch film (founder on camera) — NOT LIVE: flag-gated in
          sections/launch-film.tsx, preview with ?launchvideo=1. Placement:
          after the product proof, before the social proof — the human face
          bridges "here's the app" and "here's who vouches for it". */}
      <LaunchFilm />
      <Testimonials />
      <WhyBento />
      {/* the re-hook (moved from first-after-hero, founder 2026-07-16): you've seen
          everything ClipRadar does — remember the pain, then the receipts, then the price */}
      <FilenameWall />
      <Compare />
      <Breather name="Rado G." />
      {/* the one full-bleed colour surface — a break in the dark scroll and the
          mid-page place to convert. Sits AFTER the comparison and the tester
          quote so the order is proof → ask, and immediately before Pricing. */}
      <AccentCTA />
      <Pricing />
      <Roadmap />
      <FAQ />
      <div id="join"><FinalCTA /></div>
      </main>
      <BigFooter />
    </div>
  );
}
